import { useState, useCallback, useEffect } from 'react'
import { iFullStoreState, UserAuth } from '../../../shared/interfaces'
import { ISafetyTeamRecord, IMinimalPerson, ISafetyTeamDocument } from '../shared/types'

import {
  addSafetyTeamRecord,
  deleteSafetyTeamRecord,
  getSafetyTeamRecords,
  updateSafetyTeamRecord
} from '../shared/safety-team-db'

import { useSelector } from 'react-redux'
import moment from 'moment-timezone'

export const useSafetyTeam = () => {
  const authUser = useSelector<iFullStoreState, UserAuth | undefined>(
    (s) => s.auth?.user ?? undefined
  )

  const [activeRecords, setActiveRecords] = useState<ISafetyTeamRecord[]>([])
  const [archivedRecords, setArchivedRecords] = useState<ISafetyTeamRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: 'delete' | 'reopen' | 'deleteArchived' | null;
    recordId: string | null;
  }>({
    open: false,
    action: null,
    recordId: null,
  });

  useEffect(() => {
    const fetchRecords = async () => {
      if (!authUser) return;

      setIsLoading(true);

      try {
        const recordsFromDb: Record<string, ISafetyTeamRecord> = await getSafetyTeamRecords();

        const recordsArray: ISafetyTeamRecord[] = Object.values(recordsFromDb).map(
          (record) => ({
            ...record,
            isEditing: false,
            documents: record.documents || [],
          })
        );

        const active = recordsArray.filter((r) => !r.archivedDate);
        const archived = recordsArray.filter((r) => r.archivedDate);

        setActiveRecords(active);
        setArchivedRecords(archived);
      } catch (error) {
        console.error('Failed to fetch safety team records:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecords();
  }, [authUser]);

  const formatDate = useCallback((date: Date) => {
    return moment(date).format('MM-DD-YYYY');
  }, []);

  const handlePersonSelect = useCallback(async (person: IMinimalPerson) => {
    const newRecord: ISafetyTeamRecord = {
      id: `safety-team-${Date.now()}`,
      driver: person,
      driversLicenseNumber: '',
      driverType: '',
      testDate: '',
      assignedDate: formatDate(new Date()),
      documents: [],
      archivedDate: '',
      isEditing: true,
    }

    setActiveRecords((prev) => [newRecord, ...prev])

    if (authUser) {
      await addSafetyTeamRecord(authUser)(newRecord)
    }
  }, [formatDate, authUser])

  const handleAddNewRecord = useCallback(() => {
    setActiveRecords((prev) =>
      prev.map((record) => ({ ...record, isEditing: false }))
    )

    const newRecord = {
        id: 'new_temp_id_' + Date.now(),
        driver: null,
        driversLicenseNumber: "",
        driverType: "",
        testDate: "",
        assignedDate: "",
        documents: [],
        archivedDate: "",
        isEditing: true,
        isNew: true
    };

    setActiveRecords((prev) => [newRecord, ...prev]);

  }, [handlePersonSelect])

  const handleSave = useCallback(async (recordId: string) => {
    let updatedRecord: ISafetyTeamRecord | undefined

    setActiveRecords((prev) =>
      prev.map((record) => {
        if (record.id === recordId) {
          updatedRecord = { ...record, isEditing: false }
          return updatedRecord
        }
        return record
      })
    )

    if (updatedRecord && authUser) {
      await updateSafetyTeamRecord(authUser)(recordId, updatedRecord)
    }
  }, [authUser])

  const handleEdit = useCallback((recordId: string) => {
    setActiveRecords((prev) =>
      prev.map((record) =>
        record.id === recordId ? { ...record, isEditing: true } : record
      )
    )
  }, [])

  const handleDelete = useCallback((recordId: string) => {
    setConfirmModal({
      open: true,
      action: 'delete',
      recordId
    })
  }, [])

  const handleArchiveRecord = useCallback(async (recordId: string, date: string) => {
    const recordToArchive = activeRecords.find((record) => record.id === recordId)

    if (recordToArchive) {
      setActiveRecords((prev) =>
        prev.filter((record) => record.id !== recordId)
      )

      const archived = {
        ...recordToArchive,
        archivedDate: date || formatDate(new Date()),
        isEditing: false,
      }

      setArchivedRecords((prev) => [
        ...prev,
        archived,
      ])

      if (authUser) {
        await updateSafetyTeamRecord(authUser)(recordId, archived)
      }
    }
  }, [formatDate, activeRecords, authUser])

  const handleReopenRecord = useCallback((recordId: string) => {
    setConfirmModal({
      open: true,
      action: 'reopen',
      recordId
    })
  }, [])

  const handleDeleteArchived = useCallback((recordId: string) => {
    setConfirmModal({
      open: true,
      action: 'deleteArchived',
      recordId
    })
  }, [])

  const handleConfirmAction = useCallback(async () => {
    const { action, recordId } = confirmModal

    if (!recordId || !action) return

    switch (action) {
      case 'delete':
        setActiveRecords((prev) => prev.filter((record) => record.id !== recordId))
        if (authUser) {
          await deleteSafetyTeamRecord(authUser)(recordId)
        }
        break

      case 'reopen':
        const recordToReopen = archivedRecords.find((record) => record.id === recordId)
        if (recordToReopen) {
          setArchivedRecords((prev) => prev.filter((record) => record.id !== recordId))
          const active = {
            ...recordToReopen,
            archivedDate: '',
            isEditing: false,
          }
          setActiveRecords((prev) => [active, ...prev])
          if (authUser) {
            await updateSafetyTeamRecord(authUser)(recordId, active)
          }
        }
        break

      case 'deleteArchived':
        setArchivedRecords((prev) => prev.filter((record) => record.id !== recordId))
        if (authUser) {
          await deleteSafetyTeamRecord(authUser)(recordId)
        }
        break
    }

    setConfirmModal({
      open: false,
      action: null,
      recordId: null
    })
  }, [confirmModal, authUser, archivedRecords])

  const handleCloseModal = useCallback(() => {
    setConfirmModal({
      open: false,
      action: null,
      recordId: null
    })
  }, [])

  const handleFieldChange = useCallback((
    recordId: string,
    field: keyof ISafetyTeamRecord,
    value: string
  ) => {
    setActiveRecords((prev) =>
      prev.map((record) =>
        record.id === recordId ? { ...record, [field]: value } : record
      )
    )

    if (field === 'archivedDate' && value.trim() !== '') {
      setTimeout(() => {
        handleArchiveRecord(recordId, value)
      }, 100)
    }
  }, [handleArchiveRecord])

  const handleDriverSelect = useCallback((
    recordId: string,
    person: IMinimalPerson
  ) => {
    setActiveRecords((prev) =>
      prev.map((record) =>
        record.id === recordId ? { ...record, driver: person } : record
      )
    )
  }, [])

  const handleDocumentChange = useCallback((
    recordId: string,
    documents: ISafetyTeamDocument[]
  ) => {
    setActiveRecords((prev) => {
      const record = prev.find(r => r.id === recordId)

      if (record) {
        const currentDocs = record.documents || []

        const currentNames = new Set(currentDocs.map(d => d.storageName))
        const newNames = new Set(documents.map(d => d.storageName))

        if (currentNames.size === newNames.size &&
            [...currentNames].every(name => newNames.has(name))) {
          return prev
        }
      }

      return prev.map((record) =>
        record.id === recordId ? { ...record, documents } : record
      )
    })
  }, [])

  return {
    activeRecords,
    archivedRecords,
    isLoading,
    confirmModal,
    handleAddNewRecord,
    handleSave,
    handleEdit,
    handleDelete,
    handleFieldChange,
    handleDriverSelect,
    handleDocumentChange,
    handleReopenRecord,
    handleDeleteArchived,
    handleConfirmAction,
    handleCloseModal
  }
}
